/**
 * The dashboard - today at a glance.
 *
 * Built for the owner walking in at 4pm, not for an analyst. Everything on it
 * answers a question someone actually asks: how much have we taken, what is
 * selling, when are we busy, what have we run out of, and is anything stuck on
 * its way to the tax authority.
 */

import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';
import {
    FiShoppingCart, FiDollarSign, FiUsers, FiTrendingUp,
    FiAlertTriangle, FiPackage, FiSend, FiClock, FiCreditCard
} from 'react-icons/fi';

import { reportApi } from '../services/endpoints';
import useAsync from '../hooks/useAsync';
import { PageHeader, EmptyState } from '../components/ui/Feedback';
import { SpinnerBlock } from '../components/ui/Spinner';
import { money, compactMoney, toInputDate } from '../utils/format';
import { selectFiscal } from '../store/uiSlice';

const DashboardPage = () => {
    const navigate = useNavigate();
    const fiscal = useSelector(selectFiscal);
    const [date, setDate] = useState(toInputDate(new Date()));

    const load = useCallback(() => reportApi.dashboard({ fromDate: date }), [date]);
    const { data, loading } = useAsync(load, [date]);

    if (loading && !data) return <SpinnerBlock label="Loading the dashboard…" />;

    const summary = data?.summary || {};
    const hourly = (data?.hourly || []).map((row) => ({
        hour: `${String(row.Sales_Hour).padStart(2, '0')}:00`,
        sales: Number(row.Total_Sales),
        orders: row.Order_Count
    }));

    // The busiest hour is worth marking - it is the staffing decision the chart
    // exists to inform.
    const peakSales = Math.max(0, ...hourly.map((h) => h.sales));

    return (
        <div className="page-scroll mx-auto w-full lg:max-w-[1400px]">
            {/* Header Area */}
            <div
                className="relative mb-2 flex shrink-0 flex-col justify-between gap-2 overflow-hidden
                           rounded-2xl p-2 shadow-md sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                style={{ background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)' }}
            >
                {/* Subtle glow effect */}
                <div className="absolute -left-10 -top-10 h-32 w-32 rounded-full bg-white/10 blur-3xl pointer-events-none" />

                <div className="relative z-10">
                    <h1 className="text-lg font-black tracking-tight text-white drop-shadow-sm sm:text-2xl">Dashboard</h1>
                    <p className="mt-1 text-[13px] font-bold tracking-wider text-white/70">Today's trading at a glance</p>
                </div>
                <div className="relative z-10">
                    <input
                        type="date"
                        className="h-11 w-full cursor-pointer rounded-xl border border-white/20 bg-white/10 px-4 text-sm font-bold text-white shadow-inner outline-none backdrop-blur-md transition-all hover:bg-white/20 focus:border-white/40 focus:bg-white/20 focus:ring-4 focus:ring-white/10 sm:h-10 sm:w-44"
                        style={{ colorScheme: 'dark' }}
                        value={date}
                        max={toInputDate(new Date())}
                        onChange={(event) => setDate(event.target.value)}
                    />
                </div>
            </div>

            {/* Headline numbers */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
                {/* Highlight/Primary Card */}
                <div
                    className="relative overflow-hidden rounded-2xl p-2 shadow-lg flex flex-col justify-between group cursor-default transition-transform hover:-translate-y-1"
                    style={{ background: 'linear-gradient(135deg, #cac422ff 100%, #dce653ff 0%)' }}
                >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-2xl pointer-events-none group-hover:bg-white/20 transition-colors" />
                    <div className="flex items-center justify-between relative z-10">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-white sm:text-sm">Total Sales</p>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/20 text-white shadow-sm backdrop-blur-md sm:h-10 sm:w-10">
                            <FiDollarSign className="h-5 w-5" />
                        </div>
                    </div>
                    <div className="mt-2 relative z-10">
                        <h2 className="tabular text-lg font-black tracking-tight text-white drop-shadow-sm xs:text-2xl sm:text-3xl">
                            <span className="mr-1 text-sm text-white/90 xs:text-lg sm:text-xl">Rs.</span>
                            {money(summary.totalSales)}
                        </h2>
                        <p className="mt-1 text-[11px] font-semibold text-white/90 sm:text-xs">
                            {summary.orderCount || 0} orders today
                        </p>
                    </div>
                </div>

                {/* Card 2 */}
                <div className="rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-800 sm:text-sm">Avg Ticket</p>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 sm:h-10 sm:w-10">
                            <FiTrendingUp className="h-5 w-5" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h2 className="tabular text-lg font-black tracking-tight text-slate-800 xs:text-2xl sm:text-3xl">
                            <span className="mr-1 text-sm text-slate-600 xs:text-lg sm:text-xl">Rs.</span>
                            {money(summary.averageTicket)}
                        </h2>
                        <p className="mt-1 text-[11px] font-semibold text-slate-600 sm:text-xs">
                            {summary.guestCount || 0} guests served
                        </p>
                    </div>
                </div>

                {/* Card 3 */}
                <div className="rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-800 sm:text-sm">Sales Tax</p>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 sm:h-10 sm:w-10">
                            <FiShoppingCart className="h-5 w-5" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h2 className="tabular text-lg font-black tracking-tight text-slate-800 xs:text-2xl sm:text-3xl">
                            <span className="mr-1 text-sm text-slate-600 xs:text-lg sm:text-xl">Rs.</span>
                            {money(summary.totalTax)}
                        </h2>
                        <p className="mt-1 text-[11px] font-semibold text-slate-600 sm:text-xs">
                            Payable to the authority
                        </p>
                    </div>
                </div>

                {/* Card 4 */}
                <div className="rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm transition-shadow hover:shadow-md flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-800 sm:text-sm">Discounts</p>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 sm:h-10 sm:w-10">
                            <FiUsers className="h-5 w-5" />
                        </div>
                    </div>
                    <div className="mt-2">
                        <h2 className="tabular text-lg font-black tracking-tight text-slate-800 xs:text-2xl sm:text-3xl">
                            <span className="mr-1 text-sm text-slate-600 xs:text-lg sm:text-xl">Rs.</span>
                            {money(summary.totalDiscount)}
                        </h2>
                        <p className="mt-1 text-[11px] font-semibold text-slate-600 sm:text-xs">
                            {summary.totalSales > 0
                                ? `${((summary.totalDiscount / (summary.totalSales + summary.totalDiscount)) * 100).toFixed(1)}% of gross revenue`
                                : '—'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Alerts Section */}
            {(fiscal.failed > 0 || data?.lowStock?.length > 0) && (
                <div className="mt-2 grid gap-2 sm:gap-4 md:grid-cols-2">
                    {fiscal.failed > 0 && (
                        <button
                            type="button"
                            onClick={() => navigate('/fiscal')}
                            className="group flex items-start gap-3 rounded-2xl border border-red-200/60 bg-red-50 p-3 text-left sm:gap-4 sm:p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                        >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 group-hover:scale-110 transition-transform">
                                <FiSend className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-red-900 sm:text-base">
                                    {fiscal.failed} invoice(s) were rejected
                                </p>
                                <p className="mt-1 text-xs font-medium leading-snug text-red-700/80 sm:text-sm">
                                    They will not reach {fiscal.activeAuthorityCode || 'the authority'} until someone reviews them.
                                </p>
                            </div>
                        </button>
                    )}

                    {data?.lowStock?.length > 0 && (
                        <button
                            type="button"
                            onClick={() => navigate('/inventory')}
                            className="group flex items-start gap-3 rounded-2xl border border-amber-200/60 bg-amber-50 p-3 text-left sm:gap-4 sm:p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                        >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 group-hover:scale-110 transition-transform">
                                <FiAlertTriangle className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-amber-900 sm:text-base">
                                    {data.lowStock.length} item(s) running low
                                </p>
                                <p className="mt-1 truncate text-sm font-medium text-amber-700/80">
                                    {data.lowStock.slice(0, 3).map((s) => s.Stock_Item_Name).join(', ')}
                                    {data.lowStock.length > 3 && ` and ${data.lowStock.length - 3} more`}
                                </p>
                            </div>
                        </button>
                    )}
                </div>
            )}

            {/* Main Content Grid */}
            <div className="mt-2 grid gap-2 sm:gap-4 lg:grid-cols-3">
                {/* Sales Chart */}
                <section className="rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm lg:col-span-2">
                    <div className="mb-2 flex items-center justify-between">
                        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 sm:text-base">
                            <FiClock className="h-5 w-5 text-brand-600" />
                            Sales Activity by Hour
                        </h2>
                    </div>

                    {hourly.length === 0 ? (
                        <EmptyState title="No sales yet today" icon={FiShoppingCart} />
                    ) : (
                        <div className="h-56 sm:h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={hourly} margin={{ top: 10, right: 4, left: -24, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
                                    <XAxis
                                        dataKey="hour"
                                        tick={{ fontSize: 10, fill: '#00000', fontWeight: 600 }}
                                        axisLine={{ stroke: '#e2e8f0', strokeWidth: 2 }}
                                        tickLine={false}
                                        interval="preserveStartEnd"
                                        minTickGap={12}
                                        dy={10}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 10, fill: '#00000', fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={44}
                                        tickFormatter={compactMoney}
                                        dx={-10}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        formatter={(value, name) =>
                                            name === 'sales' ? [`Rs. ${money(value)}`, 'Sales'] : [value, 'Orders']}
                                        contentStyle={{
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            padding: '8px 12px'
                                        }}
                                    />
                                    <Bar dataKey="sales" radius={[6, 6, 0, 0]} maxBarSize={48}>
                                        {hourly.map((entry, index) => (
                                            <Cell
                                                key={index}
                                                fill={entry.sales === peakSales ? 'url(#peakGradient)' : '#cbd5e1'}
                                                className="transition-all duration-300 hover:opacity-80"
                                            />
                                        ))}
                                    </Bar>
                                    <defs>
                                        <linearGradient id="peakGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#0f1f5c" />
                                            <stop offset="100%" stopColor="#152fe1" />
                                        </linearGradient>
                                    </defs>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </section>

                {/* Right Column: Payments & Orders */}
                <section className="flex flex-col gap-1">
                    {/* Payments */}
                    <div className="rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm">
                        <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800 sm:mb-5 sm:text-base">
                            <FiCreditCard className="h-5 w-5 text-brand-600" />
                            Payment Breakdown
                        </h2>

                        {!data?.byPaymentMode?.length ? (
                            <EmptyState title="No payments yet" icon={FiDollarSign} />
                        ) : (
                            <ul className="space-y-4">
                                {data.byPaymentMode.map((row, idx) => {
                                    const share = summary.totalSales > 0
                                        ? (Number(row.Total_Amount) / summary.totalSales) * 100
                                        : 0;

                                    // Give top payment method the brand color, others neutral
                                    const barColor = idx === 0 ? 'bg-brand-600' : 'bg-slate-300';

                                    return (
                                        <li key={row.Payment_Mode_Code} className="group">
                                            <div className="mb-1.5 flex items-baseline justify-between text-sm">
                                                <span className="font-semibold text-slate-600">{row.Payment_Mode_Name}</span>
                                                <span className="tabular font-black text-slate-800">
                                                    {money(row.Total_Amount)}
                                                </span>
                                            </div>
                                            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
                                                    style={{ width: `${Math.min(100, share)}%` }}
                                                />
                                            </div>
                                            <p className="mt-1 tabular text-right text-[10px] font-bold text-slate-600">
                                                {share.toFixed(1)}%
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Order Types */}
                    <div className="rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm flex-1">
                        <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800 sm:text-base">
                            <FiPackage className="h-5 w-5 text-brand-600" />
                            Order Types
                        </h2>

                        {!data?.byOrderType?.length ? (
                            <p className="text-sm font-medium text-slate-400">No orders yet.</p>
                        ) : (
                            <ul className="space-y-2">
                                {data.byOrderType.map((row) => (
                                    <li key={row.Order_Type_Name} className="flex items-center justify-between rounded-xl bg-slate-50 p-3 transition-colors hover:bg-slate-100">
                                        <span className="font-semibold text-slate-700 flex items-center gap-2">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] font-black text-slate-600 shadow-sm">
                                                {row.Order_Count}
                                            </span>
                                            {row.Order_Type_Name}
                                        </span>
                                        <span className="tabular font-black text-slate-800">
                                            Rs. {money(row.Total_Sales)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>

            {/* Top Sellers Table */}
            <section className="mt-2 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                <div className="border-b border-slate-100 p-3">
                    <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 sm:text-base">
                        <FiTrendingUp className="h-5 w-5 text-emerald-500" />
                        Top Sellers Today
                    </h2>
                </div>

                {!data?.topItems?.length ? (
                    <div className="p-4 sm:p-8">
                        <EmptyState title="Nothing sold yet" icon={FiPackage} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-600 text-[13px] font-bold uppercase tracking-wider text-white">
                                <tr>
                                    <th className="w-10 px-2 py-3 text-center sm:w-12 sm:px-5 sm:py-4">#</th>
                                    <th className="px-2 py-3 sm:px-5 sm:py-4">Item Details</th>
                                    <th className="px-2 py-3 text-right sm:px-5 sm:py-4">Units Sold</th>
                                    <th className="px-2 py-3 text-right sm:px-5 sm:py-4">Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.topItems.map((item, index) => (
                                    <tr key={`${item.Item_Name}-${item.Variant_Name || ''}`} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="tabular px-2 py-2.5 text-center font-bold text-slate-700 sm:px-5 sm:py-3">
                                            {index + 1}
                                        </td>
                                        <td className="px-2 py-2.5 sm:px-5 sm:py-3">
                                            <p className="font-bold text-slate-800">{item.Item_Name}</p>
                                            {item.Variant_Name && (
                                                <p className="text-[11px] font-medium text-slate-500">{item.Variant_Name}</p>
                                            )}
                                        </td>
                                        <td className="tabular px-2 py-2.5 text-right font-bold text-slate-700 sm:px-5 sm:py-3">
                                            <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2.5 py-1 text-xs">
                                                {Number(item.Quantity_Sold)}
                                            </span>
                                        </td>
                                        <td className="tabular px-2 py-2.5 text-right font-black text-emerald-600 sm:px-5 sm:py-3">
                                            Rs. {money(item.Total_Amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default DashboardPage;
